import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { IndustrySlug } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CryptoIndustry } from '@/components/landing/industry/crypto'
import { GamingIndustry } from '@/components/landing/industry/gaming'
import { PrepaidIndustry } from '@/components/landing/industry/prepaid'
import { industries } from '@/constants/content'

const META: Record<IndustrySlug, { title: string, description: string }> = {
    crypto: {
        title: 'Crypto',
        description: 'Unlocking the power of Adaptive AI for the crypto revolution. Stop using AML tools to fight payment fraud and streamline your fiat-to-crypto onramp with nSure.ai.'
    },
    gaming: {
        title: 'Gaming',
        description: 'Don’t let caution keep you out of the game. Existing payment fraud solutions decline as many as 20% of gaming transactions; nSure.ai’s Adaptive AI levels the playing field.'
    },
    'prepaid-cards': {
        title: 'Prepaid & Gift Cards',
        description: 'Deliver prepaid and gift cards in an instant with real-time payment fraud prevention from nSure.ai, born on a gift card platform in 2013.'
    }
}

const PAGES: Record<IndustrySlug, FC> = {
    crypto: CryptoIndustry,
    gaming: GamingIndustry,
    'prepaid-cards': PrepaidIndustry
}

interface IndustryPageProps {
    slug: IndustrySlug
}

const IndustryPage: FC<IndustryPageProps> = ({ slug }): ReactNode => {
    const industry = industries.find(item => item.slug === slug) ?? industries[0]
    const Content = PAGES[industry.slug]
    const meta = META[industry.slug]

    return (
        <Layout tone={industry.tone}>
            <Seo title={meta.title} description={meta.description} />
            <Content />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: industries.map(industry => ({ params: { slug: industry.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<IndustryPageProps> = async ({ params }) => {
    const slug = params?.slug

    if (typeof slug !== 'string' || !industries.some(industry => industry.slug === slug)) {
        return { notFound: true }
    }

    return { props: { slug: slug as IndustrySlug } }
}

export default IndustryPage
